/* @HEADER
 * @version 1.1.1592 | 2026-11-04
 * @purpose Opens a link in the address: asks whether the person may see what it leads to now, shows why it did not open, moves to the link's project first when it differs from the one on screen, then takes the person into the space and brings the board into view; landing consumes the link.
 * @sidecar useNavLinkOpen.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8356, ZVX-DEV-8330, ZVX-DEV-8278, ZVX-DEV-7134

/**
 * Opens a link (ZVX-4464): reads the address from the hash, asks `judgeNavLink` whether the person may see what it leads to NOW,
 * and either shows why it did not open (a notice, never an empty screen) or takes the person there -- moving to the link's
 * project first when it is not the one on screen, then into the space, then to the board.
 *
 * Moving to another project remakes the app under the host (ZVX-DEV-2745). The address stays in the hash, so the mount that
 * follows reads the same link, finds its project on screen, and lands. That is the whole hand-over; nothing is kept in memory
 * across it, and one thing is kept in `sessionStorage` so a switch that did not take cannot be repeated for ever (a link whose
 * project is listed but cannot be switched to is shown as unavailable on its second turn).
 *
 * Landing consumes the link: the hash becomes `#tasks`, so a later remake does not pull the person back to it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getAllProjects } from '../lib/projectStore.ts';
import { PROJECT_REGISTRY_FIRST_SYNC_SETTLED_EVENT, PROJECT_REGISTRY_SYNCED_EVENT } from '../lib/projectRegistryBridge.ts';
import { navigateRoute } from '../hooks/useAuthRouteState';
import { useProjectSwitch } from './ProjectSwitchContext';
import { parseNavLink } from './navLinks.ts';
import type { NavLink } from './navLinks.ts';
import { judgeNavLink } from './navLinkResolve.ts';
import type { NavLinkPorts, NavLinkWhy } from './navLinkResolve.ts';
import { createNavLinkPorts } from './navLinkPorts.ts';
import { cardToOpen, localKeysForSpace, planLanding } from './navLinkLanding.ts';
import type { LandingSpace } from './navLinkLanding.ts';
import { focusBoardSection } from './focusBoardSection.ts';
import type { NavLinkNoticeKind } from './NavLinkNotice';

const SWITCHED_KEY = 'zvenix:nav-link-switched';

/**
 * ZVX-DEV-8330 (ZVX-4685, candidate 3): how long a link waits for the account's identity to be read before a project that names no
 * organisation is judged as one that has none, and how often it asks again meanwhile. After the bound the ordinary judgement stands.
 */
const IDENTITY_PATIENCE_MS = 6000;
const IDENTITY_RECHECK_MS = 500;

/**
 * Debug level only, and only the reason: no name, no address, no e-mail (ZVX-DEV-8330). A block body, not an expression body: the release strips
 * console.debug only when nobody uses the call's value (ZVX-4936).
 */
const traceWhy = (reason: NavLinkWhy): void => {
  console.debug('[nav-link]', reason);
};

/**
 * ZVX-DEV-8351 (ZVX-4685): the STEPS of a landing, in the same line as the reasons of a judgement, so a console says where a link from another
 * project stopped. The step only -- never an id of the project, of the space or of the address.
 */
type LandingStep = 'switching' | 'selecting-organisation' | 'not-here-yet' | 'landed' | 'landing-undone';
const traceStep = (step: LandingStep): void => {
  console.debug('[nav-link]', step);
};

/** The one remembered switch: which link, to which project. Wrapped: a storage that throws is a storage that remembers nothing. */
function readSwitched(): string | null {
  try {
    return window.sessionStorage.getItem(SWITCHED_KEY);
  } catch {
    return null;
  }
}
function writeSwitched(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(SWITCHED_KEY);
    else window.sessionStorage.setItem(SWITCHED_KEY, value);
  } catch {
    /* nothing to remember in */
  }
}

export interface NavLinkOpenDeps {
  /** The hash in force (`useAuthRouteState`). */
  hash: string;
  /** The spaces this device holds for the project on screen, and whether they have been read yet. */
  spaces: readonly LandingSpace[];
  spacesLoading: boolean;
  nav: {
    setActiveSpaceId: (id: string | null) => void;
    /** ZVX-DEV-8351: the space on screen, read only to say in the trace that a landing was undone; absent in a host that cannot say. */
    activeSpaceId?: string | null;
    setActiveView: (view: 'kanban') => void;
    /** ZVX-DEV-8332: the card a link to a card asks the space page to open; absent in a host that cannot. */
    setOpenCardOnArrival?: (request: { syncId: string; boardId: string | null } | null) => void;
  };
  /** Replaceable by a test; the real ones answer from the registry, the project list and the relay. */
  ports?: NavLinkPorts;
  focusBoard?: (boardId: string) => () => void;
  go?: (route: 'tasks') => void;
}

export interface NavLinkOpen {
  notice: NavLinkNoticeKind | null;
  retry: () => void;
  toProjects: () => void;
}

/** What stage 1 concluded for ONE hash: the notice to show, and the link when the person may go on to see it. */
interface Judged {
  hash: string;
  notice: NavLinkNoticeKind | null;
  link: NavLink | null;
}

export function useNavLinkOpen(deps: NavLinkOpenDeps): NavLinkOpen {
  const { hash, spaces, spacesLoading, nav } = deps;
  const projectSwitch = useProjectSwitch();
  const [judged, setJudged] = useState<Judged | null>(null);
  const [attempt, setAttempt] = useState(0);
  const spacesRef = useRef(spaces);
  const switchRef = useRef(projectSwitch);
  const stopFocusRef = useRef<(() => void) | null>(null);
  const landedForRef = useRef<string | null>(null);
  /** The space this link took the person into (ZVX-DEV-8351), to tell in the trace that something dropped it; `undefined` when no landing is being watched. */
  const landedSpaceRef = useRef<string | null | undefined>(undefined);
  /** Whether the landed space has been SEEN on screen yet: the host applies the landing a render later, and "not the landed space" before that is not an undoing. */
  const landedSeenRef = useRef(false);
  /** The server id of the space the link took the person into (ZVX-DEV-8356), to tell a REPLACEMENT of its row from the person leaving it. */
  const landedServerRef = useRef<string | null>(null);
  const notHereTracedRef = useRef<string | null>(null);
  /** When the wait for the identity of THIS link ends: set the first time it is judged, so a re-judgement does not start the wait over. */
  const patienceRef = useRef<{ hash: string; until: number } | null>(null);
  const focusBoard = deps.focusBoard ?? focusBoardSection;
  // Looked up when it is used, not when the hook renders: nothing is navigated until a link is in the address.
  const go = deps.go ?? ((route: 'tasks') => navigateRoute(route));

  // What was concluded is about one hash; once the hash moves on (the link was consumed, or the person went elsewhere) it says nothing.
  const current = judged !== null && judged.hash === hash ? judged : null;
  const plan = current?.link && !spacesLoading ? planLanding(current.link, spaces) : null;
  const notice: NavLinkNoticeKind | null = plan?.kind === 'not-here-yet' ? 'not-here-yet' : (current?.notice ?? null);

  // What the judgement reads when its answer comes: the latest spaces and the latest host, not the ones of the render it began in.
  useEffect(() => {
    spacesRef.current = spaces;
    switchRef.current = projectSwitch;
  }, [spaces, projectSwitch]);

  // The registry's first cycle ending, or a later cycle that changed it, is what a waiting link waits for. ZVX-DEV-9022 -- and only a link
  // in the address waits: this hook lives in the root of the signed-in app, so a state set here renders the whole page, and a bump
  // with no link to judge was one render of the page for every cycle of the registry, for as long as the tab stayed open.
  const hashRef = useRef(hash);
  useEffect(() => {
    hashRef.current = hash;
  }, [hash]);
  useEffect(() => {
    const again = () => {
      if (parseNavLink(hashRef.current) !== null) setAttempt((n) => n + 1);
    };
    window.addEventListener(PROJECT_REGISTRY_FIRST_SYNC_SETTLED_EVENT, again);
    window.addEventListener(PROJECT_REGISTRY_SYNCED_EVENT, again);
    return () => {
      window.removeEventListener(PROJECT_REGISTRY_FIRST_SYNC_SETTLED_EVENT, again);
      window.removeEventListener(PROJECT_REGISTRY_SYNCED_EVENT, again);
    };
  }, []);

  // Stage 1: may the person see it now, and is it this project. Every conclusion is set from the answer, never from the effect.
  useEffect(() => {
    const link = parseNavLink(hash);
    if (link === null) {
      landedForRef.current = null;
      return;
    }
    let cancelled = false;
    let recheck: ReturnType<typeof setTimeout> | null = null;
    const ports =
      deps.ports ?? createNavLinkPorts({ localSpaceKeysFor: (id) => localKeysForSpace(spacesRef.current, id) });
    const conclude = (notice: NavLinkNoticeKind | null, goOn: boolean) => {
      if (!cancelled) setJudged({ hash, notice, link: goOn ? link : null });
    };
    if (patienceRef.current?.hash !== hash) patienceRef.current = { hash, until: Date.now() + IDENTITY_PATIENCE_MS };
    const until = patienceRef.current.until;
    const patient: NavLinkPorts = { ...ports, identityRead: () => (ports.identityRead?.() ?? true) || Date.now() >= until };
    let why: NavLinkWhy | null = null;
    void judgeNavLink(link, patient, (reason) => {
      why = reason;
      traceWhy(reason);
    }).then((verdict) => {
      if (cancelled) return;
      if (verdict.kind === 'waiting' && why === 'organisation-unread') {
        // The registry's event will not come for this; ask again shortly, until the identity is read or the patience is over.
        recheck = setTimeout(() => setAttempt((n) => n + 1), IDENTITY_RECHECK_MS);
      }
      if (verdict.kind === 'waiting') return conclude('waiting', false);
      if (verdict.kind === 'no-connection') return conclude('no-connection', false);
      if (verdict.kind === 'unavailable') return conclude('unavailable', false);
      // ZVX-DEV-8353 (ZVX-4685): the organisation of the project the link leads into is chosen FIRST, as the workspace switcher would choose it. The
      // chosen organisation is a key of its own and `switchProject` moves the project only: without this a project of another organisation is
      // "out of scope" for the shell and a placard stands where the page should be. The right was checked by the judge.
      const organisationId = verdict.project.organisationId;
      if (organisationId && ports.selectOrganisation?.(organisationId)) traceStep('selecting-organisation');
      if (!verdict.switchProject) {
        writeSwitched(null);
        return conclude(null, true);
      }
      const target = getAllProjects().find((p) => p.id === verdict.project.id);
      const marker = `${hash}\n${verdict.project.id}`;
      // No way to switch, or one that was tried for this very link and left the person where they were: nothing opens.
      const switcher = switchRef.current;
      if (!target || !switcher || readSwitched() === marker) {
        writeSwitched(null);
        traceWhy('switch-not-taken');
        return conclude('unavailable', false);
      }
      writeSwitched(marker);
      conclude('waiting', false);
      traceStep('switching');
      void switcher.switchProject(target);
    });
    return () => {
      cancelled = true;
      if (recheck !== null) clearTimeout(recheck);
    };
    // `deps.ports` is a test seam and does not change within a mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, attempt]);

  // Stage 2: the project is on screen and the person may see the space; take them there, once per link.
  useEffect(() => {
    if (plan === null || plan.kind === 'not-here-yet' || landedForRef.current === hash) return;
    landedForRef.current = hash;
    landedSpaceRef.current = plan.kind === 'space' ? plan.spaceId : undefined;
    landedSeenRef.current = false;
    landedServerRef.current = plan.kind === 'space' ? (spacesRef.current.find((space) => space.id === plan.spaceId)?.serverSpaceId ?? null) : null;
    traceStep('landed');
    if (plan.kind === 'project') nav.setActiveSpaceId(null);
    else {
      nav.setActiveSpaceId(plan.spaceId);
      nav.setActiveView('kanban');
    }
    // ZVX-DEV-8332 (ZVX-4645): the card the link names is asked of the page that will draw the space; the link itself is consumed below, so
    // this is the only place the intention survives. Only on landing in a space: a refused or not-here-yet link never reaches here.
    const card = plan.kind === 'space' && current?.link ? cardToOpen(current.link) : null;
    if (card) nav.setOpenCardOnArrival?.(card);
    // Consumes the link: the hash becomes #tasks, so a later remake does not pull the person back to it.
    go('tasks');
    stopFocusRef.current?.();
    stopFocusRef.current = plan.kind === 'space' && plan.boardId !== null ? focusBoard(plan.boardId) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, hash]);

  // ZVX-DEV-8351 -- the space the link is waiting for is not on the device yet: said once per link.
  useEffect(() => {
    if (plan?.kind === 'not-here-yet' && notHereTracedRef.current !== hash) {
      notHereTracedRef.current = hash;
      traceStep('not-here-yet');
    }
  }, [plan?.kind, hash]);

  // ZVX-DEV-8351 -- a landing that something undid: after the person was taken into the space, the space on screen is no longer it. Said once.
  useEffect(() => {
    const landed = landedSpaceRef.current;
    if (landed === undefined || nav.activeSpaceId === undefined) return;
    if (nav.activeSpaceId === landed) {
      landedSeenRef.current = true;
      return;
    }
    if (!landedSeenRef.current) return;
    // ZVX-DEV-8356: the reconcile replaced the row the link landed on by another row of the SAME server space and the space on screen followed
    // it (ZVX-DEV-8355). That is not the person leaving: keep watching the landing under the new row. Only when the landed row is gone from the
    // device and the space on screen is a row of the same server space; a row still there, or one of another space, is a move.
    const serverId = landedServerRef.current;
    const rows = spacesRef.current;
    if (
      serverId !== null &&
      !rows.some((space) => space.id === landed) &&
      rows.some((space) => space.id === nav.activeSpaceId && space.serverSpaceId === serverId)
    ) {
      landedSpaceRef.current = nav.activeSpaceId;
      return;
    }
    landedSpaceRef.current = undefined;
    landedSeenRef.current = false;
    traceStep('landing-undone');
  }, [nav.activeSpaceId]);

  // A board being waited for is not waited for after the person moves on or the app goes.
  useEffect(
    () => () => {
      stopFocusRef.current?.();
      stopFocusRef.current = null;
    },
    [],
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const toProjects = useCallback(() => {
    setJudged(null);
    writeSwitched(null);
    nav.setActiveSpaceId(null);
    go('tasks');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { notice, retry, toProjects };
}
